import { baseApi } from "../../../services/rtkApi/baseApi";
import type {
  AdminParticipants,
  AdminSeason,
  AdminSeasonStatus,
  AdminSeasonSummary,
  AdminSettleResult,
  GameErrorBody,
  GameReach,
  JoinResult,
  MyGame,
  Roster,
  Season,
  SeasonInputBody,
  Standings,
} from "./types";

// The game (2026 season) on the backend's /api/game routes. baseApi's base URL
// already ends in /api. Responses come wrapped as { success, data }; each
// endpoint hands back `data` alone.

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

function unwrap<T>(res: Envelope<T>): T {
  return res.data as T;
}

export const gameApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentSeason: builder.query<Season | null, void>({
      query: () => "/game/current",
      transformResponse: (res: Envelope<Season | null>) => res.data ?? null,
      providesTags: ["Game"],
    }),
    getSeasonRoster: builder.query<Roster, string>({
      query: (id) => `/game/${encodeURIComponent(id)}/roster`,
      transformResponse: (res: Envelope<Roster>) => unwrap(res),
      providesTags: [{ type: "Game", id: "roster" }],
    }),
    getSeasonStandings: builder.query<Standings, string>({
      query: (id) => `/game/${encodeURIComponent(id)}/standings`,
      transformResponse: (res: Envelope<Standings>) => unwrap(res),
      providesTags: [{ type: "Game", id: "standings" }],
    }),
    getMyGame: builder.query<MyGame, string>({
      query: (id) => `/game/${encodeURIComponent(id)}/me`,
      transformResponse: (res: Envelope<MyGame>) => unwrap(res),
      providesTags: [{ type: "Game", id: "me" }],
    }),
    getGameReach: builder.query<GameReach, { fresh?: boolean } | void>({
      query: (arg) => ({
        url: "/game/reach",
        params: arg && arg.fresh ? { fresh: 1 } : undefined,
      }),
      transformResponse: (res: Envelope<GameReach>) => unwrap(res),
      keepUnusedDataFor: 0,
    }),
    joinSeason: builder.mutation<JoinResult, string>({
      query: (id) => ({ url: `/game/${encodeURIComponent(id)}/join`, method: "POST", body: {} }),
      transformResponse: (res: Envelope<JoinResult>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),

    // ── admin (/admin/game). Every write invalidates "Game", so the public
    // page's cache entries refresh too.
    getAdminSeasons: builder.query<AdminSeasonSummary[], void>({
      query: () => "/game/admin/seasons",
      transformResponse: (res: Envelope<{ seasons: AdminSeasonSummary[] }>) => res.data?.seasons ?? [],
      providesTags: ["Game"],
    }),
    getAdminCurrentSeason: builder.query<AdminSeason | null, void>({
      query: () => "/game/admin/seasons/current",
      transformResponse: (res: Envelope<AdminSeason | null>) => res.data ?? null,
      providesTags: ["Game"],
    }),
    getAdminSeason: builder.query<AdminSeason, string>({
      query: (id) => `/game/admin/seasons/${encodeURIComponent(id)}`,
      transformResponse: (res: Envelope<AdminSeason>) => unwrap(res),
      providesTags: ["Game"],
    }),
    getAdminParticipants: builder.query<AdminParticipants, string>({
      query: (id) => `/game/admin/${encodeURIComponent(id)}/participants`,
      transformResponse: (res: Envelope<AdminParticipants>) => unwrap(res),
      providesTags: ["Game"],
    }),
    createAdminSeason: builder.mutation<AdminSeason, SeasonInputBody>({
      query: (body) => ({ url: "/game/admin/seasons", method: "POST", body }),
      transformResponse: (res: Envelope<AdminSeason>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    updateAdminSeason: builder.mutation<AdminSeason, { id: string; body: SeasonInputBody }>({
      query: ({ id, body }) => ({ url: `/game/admin/seasons/${encodeURIComponent(id)}`, method: "PATCH", body }),
      transformResponse: (res: Envelope<AdminSeason>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    transitionAdminSeason: builder.mutation<AdminSeason, { id: string; status: AdminSeasonStatus }>({
      query: ({ id, status }) => ({ url: `/game/admin/${encodeURIComponent(id)}/transition`, method: "POST", body: { status } }),
      transformResponse: (res: Envelope<AdminSeason>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    settleAdminDay: builder.mutation<AdminSettleResult, { id: string; dayKey: string }>({
      // admin settles never finalize or notify from this page: the listener and
      // the hourly sweep own finalizing and the day's messages
      query: ({ id, dayKey }) => ({ url: `/game/admin/${encodeURIComponent(id)}/settle`, method: "POST", body: { dayKey } }),
      transformResponse: (res: Envelope<AdminSettleResult>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    resettleAdminSeason: builder.mutation<{ days: number }, string>({
      query: (id) => ({ url: `/game/admin/${encodeURIComponent(id)}/resettle`, method: "POST", body: {} }),
      transformResponse: (res: Envelope<{ days: number }>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    addAdminParticipant: builder.mutation<{ inserted: boolean }, { id: string; username: string }>({
      query: ({ id, username }) => ({ url: `/game/admin/${encodeURIComponent(id)}/participants`, method: "POST", body: { username } }),
      transformResponse: (res: Envelope<{ inserted: boolean }>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
    removeAdminParticipant: builder.mutation<{ removed: boolean; participantCount: number }, { id: string; username: string }>({
      query: ({ id, username }) => ({
        url: `/game/admin/${encodeURIComponent(id)}/participants/${encodeURIComponent(username)}`,
        method: "DELETE",
      }),
      transformResponse: (res: Envelope<{ removed: boolean; participantCount: number }>) => unwrap(res),
      invalidatesTags: ["Game"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCurrentSeasonQuery,
  useGetSeasonRosterQuery,
  useGetSeasonStandingsQuery,
  useGetMyGameQuery,
  useGetGameReachQuery,
  useLazyGetGameReachQuery,
  useJoinSeasonMutation,
  useGetAdminSeasonsQuery,
  useGetAdminCurrentSeasonQuery,
  useGetAdminSeasonQuery,
  useGetAdminParticipantsQuery,
  useCreateAdminSeasonMutation,
  useUpdateAdminSeasonMutation,
  useTransitionAdminSeasonMutation,
  useSettleAdminDayMutation,
  useResettleAdminSeasonMutation,
  useAddAdminParticipantMutation,
  useRemoveAdminParticipantMutation,
} = gameApi;

/** The server's error code and plain message from an RTK Query error, if it sent one. */
export function readGameError(err: unknown): { status: number | string | null; code: string | null; message: string | null; body: GameErrorBody | null } {
  if (!err || typeof err !== "object") return { status: null, code: null, message: null, body: null };
  const e = err as { status?: number | string; data?: unknown };
  const body = e.data && typeof e.data === "object" ? (e.data as GameErrorBody) : null;
  return {
    status: e.status ?? null,
    code: body?.error ?? null,
    message: body?.message ?? null,
    body,
  };
}
